<template>
  <div class="analyses-lpu-cards-chips min-w-0 w-100 overflow-hidden">
    <div
      data-qa="analyses_lpu_cards_chips"
      class="analyses-lpu-cards-chips__row d-flex align-center flex-nowrap flex-sm-wrap ga-2 min-w-0 w-100 overflow-x-auto"
    >
      <v-chip
        data-qa="analyses_lpu_cards_filters_chip"
        class="flex-shrink-0"
        color="bg-primary"
        text-color="primary"
        @click="isFiltersDrawerOpen = true"
      >
        <v-icon size="24">
          ui-icon-sliders
        </v-icon>
      </v-chip>

      <v-chip
        data-qa="analyses_lpu_cards_sort_chip"
        class="flex-shrink-0"
        color="primary"
        text-color="bg-gray-0"
        @click="openSortPicker"
      >
        {{ appliedSortLabel }}
        <v-icon
          right
          size="18"
        >
          ui-icon-arrow-down
        </v-icon>
      </v-chip>

      <LpuCardsActiveFilterChip
        v-for="chip in activeFilterChips"
        :key="chip.chipDataQa"
        :chip-data-qa="chip.chipDataQa"
        :reset-data-qa="chip.resetDataQa"
        :label="chip.label"
        @click="chip.onClick"
        @reset="chip.onReset"
      />
    </div>

    <LpuCardsFiltersDrawer
      v-model="isFiltersDrawerOpen"
      :draft-filters="draftFilters"
      :has-unsaved-filters="hasUnsavedFilters"
      :show-reset-filters-button="showResetFiltersButton"
      :is-sort-sheet-open="isDrawerSortSheetOpen"
      :filters-count="filtersCount"
      :count-loading="isCountLoading"
      :apply-loading="applyLoading"
      @open-sort-sheet="openSortSheetFromDrawer"
      @close-sort-sheet="isDrawerSortSheetOpen = false"
      @open-place-drawer="openPlaceDrawer"
      @update:draft-filters="updateDraftFilters"
      @apply="applyFilters"
      @reset-filters="resetDraftFilters"
    />

    <LpuCardsPlaceDrawer
      v-model="isPlaceDrawerOpen"
      :town-id="townId"
      :selected-place="draftPlace"
      @back="isPlaceDrawerOpen = false"
      @apply="onPlaceApply"
      @request-error="emit('request-error', $event)"
    />

    <LpuCardsSortDialog
      v-if="!isMobile"
      v-model="isSortDialogOpen"
      :selected-sort="appliedSort"
      @confirm="onSortDialogConfirm"
    />

    <v-overlay
      v-if="isMobile"
      :value="isSortSheetOpen"
      @click="isSortSheetOpen = false"
    />
    <LpuCardsSortBottomSheet
      v-if="isMobile"
      v-model="isSortSheetOpen"
      :selected-sort="appliedSort"
      @select="onSortSelect"
    />

    <LpuCardsSortBottomSheet
      v-model="isDrawerSortSheetOpen"
      :selected-sort="draftSort"
      @select="onDrawerSortSelect"
    />
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  ref,
  toRef,
  toRefs,
  watch,
} from 'vue'
import useIsMobile from 'src/composables/useIsMobile'
import {
  type AnalysesLpuSortValue,
} from 'src/components/pages/[town-translit]/diagnostika/analiz/_constants/analyses-lpu-sort'
import {
  ANALYSES_LPU_DISCOUNT_CHIP_LABEL,
  ANALYSES_LPU_ONLINE_ORDER_CHIP_LABEL,
} from 'src/components/pages/[town-translit]/diagnostika/analiz/_constants/analyses-lpu-rating-filter'
import type { AnalysesLpuCardsFilters } from 'src/components/pages/[town-translit]/diagnostika/analiz/api/apiAnalysesLpuCardsFilter.types'
import type { AnalysesPlaceItem } from 'src/components/pages/[town-translit]/diagnostika/analiz/api/apiAnalysesDistricts.types'
import useLpuCardsChips
  from 'src/components/pages/[town-translit]/diagnostika/analiz/components/LpuCard/_composables/use-lpu-cards-chips'
import LpuCardsFiltersDrawer
  from 'src/components/pages/[town-translit]/diagnostika/analiz/components/LpuCard/LpuCardsFiltersDrawer.vue'
import LpuCardsPlaceDrawer
  from 'src/components/pages/[town-translit]/diagnostika/analiz/components/LpuCard/LpuCardsPlaceDrawer.vue'
import LpuCardsActiveFilterChip
  from 'src/components/pages/[town-translit]/diagnostika/analiz/components/LpuCard/LpuCardsActiveFilterChip.vue'
import LpuCardsSortBottomSheet
  from 'src/components/pages/[town-translit]/diagnostika/analiz/components/LpuCard/LpuCardsSortBottomSheet.vue'
import LpuCardsSortDialog
  from 'src/components/pages/[town-translit]/diagnostika/analiz/components/LpuCard/LpuCardsSortDialog.vue'

const props = defineProps<{
  appliedSort: AnalysesLpuSortValue
  appliedOnlineOrder: boolean
  appliedWithDiscount: boolean
  appliedPlace?: AnalysesPlaceItem
  applyLoading?: boolean
}>()

type Emits = {
  (_event: 'apply', _filters: AnalysesLpuCardsFilters): void
  (_event: 'request-error', _error: unknown): void
}

const emit = defineEmits<Emits>()

const {
  appliedSort,
  appliedOnlineOrder,
  appliedWithDiscount,
  applyLoading,
} = toRefs(props)
const appliedPlace = toRef(props, 'appliedPlace')

const isMobile = useIsMobile()

const isSortDialogOpen = ref(false)
const isSortSheetOpen = ref(false)
const isDrawerSortSheetOpen = ref(false)

const {
  townId,
  isFiltersDrawerOpen,
  isPlaceDrawerOpen,
  draftSort,
  draftPlace,
  draftFilters,
  filtersCount,
  isCountLoading,
  appliedSortLabel,
  hasUnsavedFilters,
  showResetFiltersButton,
  getCurrentFilters,
  openFiltersDrawer,
  openPlaceDrawer,
  onPlaceApply,
  onDrawerSortSelect,
  updateDraftFilters,
  applyFilters,
  resetDraftFilters,
  resetAppliedPlace,
  resetAppliedOnlineOrder,
  resetAppliedWithDiscount,
} = useLpuCardsChips({
  appliedSort,
  appliedOnlineOrder,
  appliedWithDiscount,
  appliedPlace,
  onApply: filters => emit('apply', filters),
  onRequestError: error => emit('request-error', error),
})

type ActiveFilterChip = {
  chipDataQa: string
  resetDataQa: string
  label: string
  onClick: () => void
  onReset: () => void
}

const activeFilterChips = computed<ActiveFilterChip[]>(() => [
  appliedPlace.value && {
    chipDataQa: 'analyses_lpu_cards_place_chip',
    resetDataQa: 'analyses_lpu_cards_place_chip_reset',
    label: appliedPlace.value.name,
    onClick: openPlaceDrawer,
    onReset: resetAppliedPlace,
  },
  appliedOnlineOrder.value && {
    chipDataQa: 'analyses_lpu_cards_online_order_chip',
    resetDataQa: 'analyses_lpu_cards_online_order_chip_reset',
    label: ANALYSES_LPU_ONLINE_ORDER_CHIP_LABEL,
    onClick: openFiltersDrawer,
    onReset: resetAppliedOnlineOrder,
  },
  appliedWithDiscount.value && {
    chipDataQa: 'analyses_lpu_cards_discount_chip',
    resetDataQa: 'analyses_lpu_cards_discount_chip_reset',
    label: ANALYSES_LPU_DISCOUNT_CHIP_LABEL,
    onClick: openFiltersDrawer,
    onReset: resetAppliedWithDiscount,
  },
].filter((chip): chip is ActiveFilterChip => Boolean(chip)))

watch(isFiltersDrawerOpen, isOpen => {
  if (!isOpen) {
    isDrawerSortSheetOpen.value = false
  }
})

function openSortPicker() {
  if (isMobile.value) {
    isSortSheetOpen.value = true
    return
  }

  isSortDialogOpen.value = true
}

function openSortSheetFromDrawer() {
  isDrawerSortSheetOpen.value = true
}

function onSortSelect(value: AnalysesLpuSortValue) {
  draftSort.value = value
  emit('apply', getCurrentFilters({}, value))
}

function onSortDialogConfirm(value: AnalysesLpuSortValue) {
  emit('apply', getCurrentFilters({}, value))
}
</script>

<style lang="scss" scoped>
.analyses-lpu-cards-chips__row {
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}
</style>
